// =============================================================================
// hooks/sales/useLeadCommercialContext.ts — Leitura do agregador de contexto
// comercial por lead (`GET /api/sales/leads/:leadId/commercial-context`,
// F38-S01) consumido pelo CRM (F38-S06, doc
// planejamento-livechat-contexto-comercial.md §3-B) e, em seguida, pelo
// mesmo bloco no live chat (F38-S04).
//
// Contrato: `CommercialContext` vem de `@elemento/shared-schemas`
// (packages/shared-schemas/src/sales-commercial-context.ts) — a MESMA fonte
// que a API usa para validar a resposta. Nenhum campo é redeclarado aqui.
// Todos os blocos internos (`campaign`, `nextAppointment`, `restrictions`)
// são nullable/[] — cache antigo do front nunca quebra o parse.
//
// Key ISOLADA (`['sales','commercial-context','lead', leadId]`, D5/plano) —
// nenhuma key existente é tocada; sem invalidação cruzada com
// `contactAttemptKeys`/`campaignKeys`/etc.
//
// Gate fail-closed próprio (`useCampaignMembershipGate`): a flag de
// composição `livechat.commercial_context.enabled` E a flag de conteúdo
// `sales.campaigns.enabled` E a permissão `sales_campaigns:read` — a
// AUSÊNCIA de qualquer uma delas mantém `enabled: false` (nenhum request
// sai) e o consumidor (CrmDetailPage) não renderiza nada no DOM (DoD do
// slot). `canOperate` acrescenta `sales_campaigns:operate` para o link
// "Operar" do card.
// =============================================================================

import type { CommercialContext } from '@elemento/shared-schemas';
import { CommercialContextSchema } from '@elemento/shared-schemas';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth-store';
import { useFeatureFlag } from '../useFeatureFlag';

export type { CommercialContext };

// ---------------------------------------------------------------------------
// Gate — flags de composição + conteúdo, permissão de leitura/operação.
// ---------------------------------------------------------------------------

const FLAG_COMPOSITION = 'livechat.commercial_context.enabled';
const FLAG_CAMPAIGNS = 'sales.campaigns.enabled';
const READ_PERMISSION = 'sales_campaigns:read';
const OPERATE_PERMISSION = 'sales_campaigns:operate';

export interface CampaignMembershipGate {
  /** true quando composição + conteúdo + permissão de leitura estão liberados. */
  canRead: boolean;
  /** true quando, além do read-gate, o ator também pode abrir a operação da campanha. */
  canOperate: boolean;
  /** true enquanto alguma das flags ainda não respondeu — evita "flash" de conteúdo. */
  isLoading: boolean;
}

export function useCampaignMembershipGate(): CampaignMembershipGate {
  const { hasPermission } = useAuth();
  const composition = useFeatureFlag(FLAG_COMPOSITION);
  const campaigns = useFeatureFlag(FLAG_CAMPAIGNS);

  const canRead = hasPermission(READ_PERMISSION) && composition.enabled && campaigns.enabled;
  const canOperate = canRead && hasPermission(OPERATE_PERMISSION);

  return {
    canRead,
    canOperate,
    isLoading: composition.isLoading || campaigns.isLoading,
  };
}

// ---------------------------------------------------------------------------
// Query key — isolada, D5 do plano.
// ---------------------------------------------------------------------------

export const leadCommercialContextKeys = {
  lead: (leadId: string) => ['sales', 'commercial-context', 'lead', leadId] as const,
};

// ---------------------------------------------------------------------------
// Fetch — GET /api/sales/leads/:leadId/commercial-context
// ---------------------------------------------------------------------------

async function fetchLeadCommercialContext(leadId: string): Promise<CommercialContext> {
  const raw = await api.get<unknown>(
    `/api/sales/leads/${encodeURIComponent(leadId)}/commercial-context`,
  );
  // Validação runtime — mesmo padrão de useCampaigns.ts: drift de contrato
  // silencioso não pode virar tela quebrada (doc 26 §9).
  return CommercialContextSchema.parse(raw);
}

export function useLeadCommercialContext(
  leadId: string,
  opts: { enabled?: boolean } = {},
): {
  context: CommercialContext | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const enabled = (opts.enabled ?? true) && leadId.length > 0;

  const { data, isLoading, isError } = useQuery({
    queryKey: leadCommercialContextKeys.lead(leadId),
    queryFn: () => fetchLeadCommercialContext(leadId),
    enabled,
    staleTime: 15_000,
  });

  return { context: data, isLoading: enabled && isLoading, isError };
}
